import React, { Component } from "react"
import { unstable_batchedUpdates } from "react-dom"
import { connect } from "react-redux"
import Routes, { offlinePages } from "./routes"
import { Sidebar, Header, ErrorBoundary } from "screens"
import {
	Content,
	RouteContent,
	Loader,
	StaffModal,
	ChatWidget,
} from "components"
import { setBreadcrumb, setNotificationToken } from "store/actions"
import { ConfigProvider, notification } from "antd"
import { LoadScript, Libraries } from "@react-google-maps/api"
import "styles/styles.scss"
import "styles/theme.less"
import strings from "utils/strings"
import pt from "antd/es/locale/pt_PT"
import en from "antd/es/locale/en_GB"
import {
	listenToMessages,
	requestNotificationPermission,
} from "firebase/firebaseMessaging"
import { onAuthStateChanged } from "firebase/auth"
import { auth } from "firebase/firebase"
import { API, Endpoints } from "utils/api"
import { isBackofficeAllowedRole } from "utils/roles"
import { setUser, setToken, setLogout } from "store/actions"

// Bootstrap CSS
import "bootstrap/dist/css/bootstrap.min.css"

const GOOGLE_MAPS_LIBRARIES: Libraries = ["places", "geometry"]

export class App extends Component<any, any> {
	unsubscribeAuth: (() => void) | null = null

	constructor(props: any) {
		super(props)

		this.state = {
			authResolved: false,
			sidebarOpen: window.innerWidth >= 992,
			sidebarHidden: false,
		}

		this.handleResize = this.handleResize.bind(this)
		window.addEventListener("resize", this.handleResize)
	}

	UNSAFE_componentWillMount() {
		strings.setLanguage(this.props.language || "pt")
	}

	componentDidUpdate(prevProps: any) {
		const { dispatch, router, user } = this.props

		if (
			prevProps?.router?.location?.pathname !== router?.location?.pathname &&
			(!router?.location?.pathname
				?.split("/")
				.some((path: string) => path === "new") ||
				router?.location?.pathname?.split("/").length !==
					prevProps?.location?.pathname?.split("/").length)
		) {
			dispatch(setBreadcrumb(null))
		}

		if (
			!this.shouldSetupNotifications(prevProps.user) &&
			this.shouldSetupNotifications(user)
		) {
			this.setupNotifications()
		}
	}

	async componentDidMount() {
		const { dispatch } = this.props

		// Keep Redux in sync with Firebase auth state across refreshes and
		// token renewals. The Login page handles the initial sign-in flow;
		// this listener only acts on changes that happen outside of it
		// (e.g. token refresh, session expiry, sign-out from another tab).
		this.unsubscribeAuth = onAuthStateChanged(auth, async (firebaseUser) => {
			if (firebaseUser) {
				try {
					const tokenResult = await firebaseUser.getIdTokenResult()
					const role = tokenResult.claims?.role as string | undefined

					if (!role || !isBackofficeAllowedRole(role)) {
						await auth.signOut()
						dispatch(setLogout())
						notification.error({
							key: "auth-not-authorized",
							message: strings.authentication.notAuthorized,
							description: strings.authentication.notAuthorizedDescription,
							placement: "bottomRight",
							duration: 5,
						})
						if (!this.state.authResolved) {
							this.setState({ authResolved: true })
						}
						return
					}

					unstable_batchedUpdates(() => {
						dispatch(setToken(tokenResult.token))
						dispatch(setUser({ ...tokenResult.claims }))
					})
				} catch {
					// Token unreadable — clear session
					dispatch(setLogout())
				} finally {
					if (!this.state.authResolved) {
						this.setState({ authResolved: true })
					}
				}
			} else {
				dispatch(setLogout())
				if (!this.state.authResolved) {
					this.setState({ authResolved: true })
				}
			}
		})

		const { user } = this.props
		if (this.shouldSetupNotifications(user)) {
			await this.setupNotifications()
		}
	}

	componentWillUnmount() {
		window.removeEventListener("resize", this.handleResize)
		this.unsubscribeAuth?.()
	}

	handleResize() {
		const isMobile = window.innerWidth < 992
		this.setState((state: any) => ({ isMobile, sidebarOpen: !isMobile }))
	}

	shouldSetupNotifications(user: any) {
		return Boolean(user && user.role)
	}

	openSidebar() {
		this.setState({ sidebarOpen: true })
	}

	closeSidebar() {
		if (document.body.clientWidth < 992) {
			this.setState({ sidebarHidden: true, sidebarOpen: false })
		} else {
			this.setState({ sidebarOpen: false })
		}
	}

	get hideNavBar() {
		const { router } = this.props
		const { location } = router
		const path = location.pathname.split("/")[1]

		return (
			offlinePages.indexOf(location.pathname) !== -1 ||
			offlinePages.indexOf(`/${path}`) !== -1
		)
	}

	async setupNotifications() {
		const { dispatch } = this.props
		const permission = await Notification.requestPermission()
		if (permission === "granted") {
			listenToMessages()
			const token = await requestNotificationPermission()
			if (token) {
				try {
					await API.post({
						url: Endpoints.uriNotifToken(),
						data: { notiftoken: token, topic: "webApp" },
					})
					dispatch(setNotificationToken(token))
				} catch (err) {
					console.error("Error saving notif token: ", err)
				}
			}
		} else if (permission === "denied") {
			console.warn("Notification permission denied")
			notification.error({
				message: strings.notifications.title,
				description: strings.notifications.permissionDenied,
				placement: "bottomRight",
				duration: 5,
			})
		}
	}

	render() {
		const { language } = this.props
		const locale = language === "pt" ? pt : en
		const appContent = (
			<div className="App">
				{!this.hideNavBar && (
					<Sidebar
						open={this.state.sidebarOpen}
						onMobile={(status: any) => status && this.closeSidebar()}
						openSidebar={() => this.openSidebar()}
						closeSidebar={() => this.closeSidebar()}
					/>
				)}
				<Content>
					{!this.hideNavBar && (
						<Header
							sidebarOpen={this.state.sidebarOpen}
							onToggleSidebar={() => {
								this.setState((state: any) => ({
									sidebarHidden: !state.sidebarHidden,
									sidebarOpen: !state.sidebarOpen,
								}))
							}}
						/>
					)}
					<ConfigProvider locale={locale}>
						<RouteContent style={this.hideNavBar ? { padding: 0 } : null}>
							<ErrorBoundary>
								<Routes authResolved={this.state.authResolved} />
							</ErrorBoundary>
						</RouteContent>
					</ConfigProvider>
				</Content>
				{!this.hideNavBar && <StaffModal />}
				{!this.hideNavBar && this.props.user && <ChatWidget />}
				<Loader />
			</div>
		)

		if (this.hideNavBar) {
			return appContent
		}

		return (
			<LoadScript
				googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY as string}
				libraries={GOOGLE_MAPS_LIBRARIES}
			>
				{appContent}
			</LoadScript>
		)
	}
}

const mapStateToProps = (state: any) => ({
	router: state.router,
	loader: state.loader,
	breadcrumb: state.breadcrumb,
	language: state.language,
	user: state.user,
	notificationToken: state.notificationToken,
})

export default connect(mapStateToProps)(App)
